'use client';

import React, { useEffect } from 'react';
import useIamStore from '../store/useIamStore';

export default function RolesPage() {
    const { roles, permissionRegistry, fetchRoles, fetchPermissionRegistry, loading, error } = useIamStore();

    useEffect(() => {
        fetchRoles();
        fetchPermissionRegistry();
    }, [fetchRoles, fetchPermissionRegistry]);

    if (!permissionRegistry || loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin h-8 w-8 border-2 border-indigo-600 border-t-transparent rounded-full" />
            </div>
        );
    }

    const categories = permissionRegistry.byCategory;

    return (
        <div className="max-w-6xl mx-auto p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Roles & Permissions</h1>

            {error && (
                <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
                    <p className="text-sm text-red-700">Error loading roles: {error}</p>
                </div>
            )}

            {roles.length === 0 && !loading && (
                <div className="mb-4 rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-3">
                    <p className="text-sm text-yellow-700">No roles found for this organization. Try creating an organization first.</p>
                </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                        <tr className="bg-gray-50">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                                Permission
                            </th>
                            {roles.map((role) => (
                                <th
                                    key={role._id}
                                    className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap"
                                >
                                    <div>{role.name}</div>
                                    <span
                                        className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                            role.scope === 'organization'
                                                ? 'bg-purple-100 text-purple-700'
                                                : 'bg-blue-100 text-blue-700'
                                        }`}
                                    >
                                        {role.scope}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {Object.entries(categories).map(([category, perms]) => (
                            <React.Fragment key={category}>
                                <tr>
                                    <td
                                        colSpan={roles.length + 1}
                                        className="px-4 py-2 bg-gray-50 text-xs font-bold text-gray-700 uppercase tracking-wider"
                                    >
                                        {category}
                                    </td>
                                </tr>
                                {perms.map((perm) => (
                                    <tr key={perm.key} className="hover:bg-gray-50">
                                        <td className="px-4 py-2.5 text-sm text-gray-800 sticky left-0 bg-white z-10">
                                            <div className="font-medium">{perm.label}</div>
                                            <div className="text-xs text-gray-400 font-mono">{perm.key}</div>
                                        </td>
                                        {roles.map((role) => {
                                            const has = role.permissions.includes(perm.key);
                                            return (
                                                <td key={role._id} className="px-4 py-2.5 text-center">
                                                    {has ? (
                                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600">
                                                            <svg
                                                                className="w-4 h-4"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                                strokeWidth={2.5}
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    d="M5 13l4 4L19 7"
                                                                />
                                                            </svg>
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-300">
                                                            <svg
                                                                className="w-4 h-4"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                                strokeWidth={2}
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    d="M6 18L18 6M6 6l12 12"
                                                                />
                                                            </svg>
                                                        </span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
